import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Plus, Trash2 } from 'lucide-react';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function TimesheetWeeklyGrid({
  weekData, weekDates, projects,
  onEditEntry, onAddEntry, onDeleteEntry, onSelectDevOpsTask
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium min-w-[200px]">Task</th>
                  {weekDates.map((date, i) => (
                    <th key={date} className="text-center p-3 font-medium min-w-[80px]">
                      <div>{DAY_NAMES[i]}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(date)}</div>
                    </th>
                  ))}
                  <th className="text-center p-3 font-medium">Total</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {weekData?.entries?.length > 0 ? (
                  Object.entries(
                    weekData.entries.reduce((acc, entry) => {
                      const key = `${entry.project_id}|${entry.task_id}`;
                      if (!acc[key]) acc[key] = { entries: [], project_id: entry.project_id, task_id: entry.task_id, source: entry.source, devops_work_item_id: entry.devops_work_item_id, project_name_display: entry.project_name_display, description: entry.description };
                      acc[key].entries.push(entry);
                      return acc;
                    }, {})
                  ).map(([key, group]) => {
                    const rowTotal = group.entries.reduce((sum, e) => sum + (e.hours_spent || 0), 0);
                    const projectName = group.project_name_display || projects.find(p => p.id === group.project_id)?.name || group.project_id;
                    const isFromDevOps = group.source === 'devops_auto_list' || group.source === 'devops_lookup';
                    
                    return (
                      <tr key={key} className="border-b hover:bg-muted/30">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-sm">{projectName}</div>
                            {isFromDevOps && <Badge className="text-xs bg-blue-100 text-blue-700">DevOps</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate max-w-[250px]">
                            {group.devops_work_item_id
                              ? <span>#{group.devops_work_item_id} - {group.description || group.task_id}</span>
                              : group.task_id}
                          </div>
                        </td>
                        {weekDates.map(date => {
                          const dayEntry = group.entries.find(e => e.date === date);
                          return (
                            <td key={date} className="text-center p-2">
                              {dayEntry ? (
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-8 w-12 font-medium"
                                  onClick={() => weekData.can_edit && onEditEntry(dayEntry)}
                                  disabled={!weekData.can_edit}
                                  data-testid={`entry-${date}`}
                                >
                                  {dayEntry.hours_spent}h
                                </Button>
                              ) : (
                                weekData.can_edit && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-8 w-12 text-muted-foreground"
                                    onClick={() => onAddEntry(date)}
                                    data-testid={`add-entry-${date}`}
                                  >
                                    -
                                  </Button>
                                )
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center p-3 font-semibold">{rowTotal}h</td>
                        <td className="p-2">
                          {weekData.can_edit && group.entries[0] && (
                            <Button
                              variant="ghost" size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => onDeleteEntry(group.entries[0].id)}
                              title="Delete this entry"
                              data-testid={`delete-entry-${key}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center py-8 text-muted-foreground">
                      No entries for this week. Click &quot;Add Entry&quot; to start logging.
                    </td>
                  </tr>
                )}
                <tr className="bg-muted/50 font-semibold">
                  <td className="p-3">Daily Total</td>
                  {weekDates.map(date => (
                    <td key={date} className="text-center p-3">
                      {weekData?.daily_totals?.[date]?.hours?.toFixed(1) || 0}h
                    </td>
                  ))}
                  <td className="text-center p-3 text-primary">{weekData?.week_total_hours?.toFixed(1) || 0}h</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* DevOps Suggestions */}
      {weekData?.can_edit && weekData?.devops_suggestions?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Azure DevOps Work Items</CardTitle>
            <CardDescription>Click to add work item to your timesheet</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {weekData.devops_suggestions.slice(0, 20).map(task => (
                <div
                  key={task.id}
                  className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => onSelectDevOpsTask(task)}
                  data-testid={`devops-task-${task.id}`}
                >
                  <Badge variant="outline" className="shrink-0 mt-0.5">#{task.id}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{task.title}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{task.project}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className="text-xs bg-blue-100 text-blue-800">{task.work_item_type}</Badge>
                      <Badge className="text-xs bg-gray-100 text-gray-800">{task.state}</Badge>
                      {task.assigned_to && <span className="text-xs text-muted-foreground">• {task.assigned_to}</span>}
                    </div>
                  </div>
                  <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
